import { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, useMap, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Globe, Users, UserPlus, Filter, X, MapPin, Zap, Crown, 
  ChevronDown, ChevronUp, Eye, EyeOff, RefreshCw, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier colors
const TIER_COLORS = {
  starter: '#6b7280', // Gray
  pro: '#3b82f6',     // Blue
  elite: '#D4AF37'    // Gold
};

// Connection colors
const CONNECTION_COLORS = {
  mutual: '#22c55e',   // Green
  one_way: '#3b82f6'   // Blue
};

// Tier Badge Component
const TierBadge = ({ tier, small = false }) => {
  const colors = {
    elite: { bg: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', border: '1px solid #D4AF37' },
    pro: { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', border: '1px solid #3b82f6' },
    starter: { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280', border: '1px solid #6b7280' }
  };
  const style = colors[tier] || colors.starter;
  
  return (
    <span 
      className={`${small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'} rounded-full font-medium`}
      style={{ background: style.bg, color: style.color, border: style.border }}
    >
      {tier?.toUpperCase()}
    </span>
  );
};

// Map bounds controller
const MapController = ({ nodes, selectedNode }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedNode) {
      map.flyTo([selectedNode.latitude, selectedNode.longitude], 8, { duration: 1 });
    } else if (nodes.length > 0) {
      const bounds = nodes.map(n => [n.latitude, n.longitude]);
      if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      } else if (bounds.length === 1) {
        map.setView(bounds[0], 5);
      }
    }
  }, [nodes, selectedNode, map]);
  
  return null;
};

// Stats Panel Component
const StatsPanel = ({ stats, isCollapsed, onToggle }) => {
  if (!stats) return null;
  
  return (
    <div 
      className="absolute top-4 left-4 z-[1000] rounded-xl shadow-lg overflow-hidden"
      style={{ background: 'rgba(10, 10, 10, 0.95)', border: '1px solid rgba(212, 175, 55, 0.3)', maxWidth: '280px' }}
    >
      <button 
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#D4AF37' }}>
          <Globe size={16} />
          Your Global Reach
        </h3>
        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>
      
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
              <p className="text-2xl font-bold" style={{ color: '#D4AF37' }}>{stats.countries_count}</p>
              <p className="text-xs text-gray-400">🌍 Countries</p>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
              <p className="text-2xl font-bold" style={{ color: '#22c55e' }}>{stats.mutual_count}</p>
              <p className="text-xs text-gray-400">🔁 Mutuals</p>
            </div>
          </div>
          
          {stats.most_connected_city && (
            <div className="p-2 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
              <p className="text-xs text-gray-400 mb-1">🏙️ Most Connected City</p>
              <p className="text-sm font-medium">{stats.most_connected_city}</p>
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs" style={{ color: '#a3a3a3' }}>
            <span>Following: {stats.following_count}</span>
            <span>Followers: {stats.followers_count}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Filter Panel Component
const FilterPanel = ({ 
  viewMode, setViewMode, 
  showConnections, setShowConnections,
  tierFilter, setTierFilter,
  accessLevel,
  onRefresh
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div 
      className="absolute top-4 right-4 z-[1000] rounded-xl shadow-lg overflow-hidden"
      style={{ background: 'rgba(10, 10, 10, 0.95)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
    >
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-white/5 transition-colors"
      >
        <Filter size={16} style={{ color: '#D4AF37' }} />
        <span className="text-sm font-medium">Filters</span>
        {isOpen ? <ChevronUp size={14} className="ml-auto" /> : <ChevronDown size={14} className="ml-auto" />}
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 space-y-3">
          {/* View Mode */}
          <div>
            <p className="text-xs text-gray-400 mb-2">View Mode</p>
            <div className="space-y-1">
              <button
                onClick={() => setViewMode('my_connections')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  viewMode === 'my_connections' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'hover:bg-white/5'
                }`}
              >
                My Connections
              </button>
              <button
                onClick={() => setViewMode('mutuals_only')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  viewMode === 'mutuals_only' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'hover:bg-white/5'
                }`}
              >
                Mutuals Only
              </button>
              {(accessLevel === 'pro' || accessLevel === 'elite') && (
                <button
                  onClick={() => setViewMode('all')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    viewMode === 'all' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'hover:bg-white/5'
                  }`}
                >
                  All DJs
                </button>
              )}
            </div>
          </div>
          
          {/* Connection Lines Toggle */}
          <div>
            <p className="text-xs text-gray-400 mb-2">Show Lines</p>
            <button
              onClick={() => setShowConnections(!showConnections)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                showConnections ? 'bg-[#D4AF37]/20' : 'hover:bg-white/5'
              }`}
            >
              <span>Connection Lines</span>
              {showConnections ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          </div>
          
          {/* Tier Filter */}
          <div>
            <p className="text-xs text-gray-400 mb-2">Filter by Tier</p>
            <div className="flex flex-wrap gap-1">
              {['all', 'elite', 'pro', 'starter'].map(t => (
                <button
                  key={t}
                  onClick={() => setTierFilter(t)}
                  className={`px-2 py-1 rounded-full text-xs transition-colors ${
                    tierFilter === t ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]' : 'border border-gray-600'
                  }`}
                >
                  {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm border border-gray-600 hover:border-[#D4AF37] transition-colors"
          >
            <RefreshCw size={14} />
            Refresh Map
          </button>
        </div>
      )}
    </div>
  );
};

// Enhanced Suggestions Panel Component
const SuggestionsPanel = ({ onFollow, isLoading, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const categories = [
    { id: 'all', label: 'Best', icon: '✨' },
    { id: 'genre', label: 'Genre', icon: '🎵' },
    { id: 'location', label: 'Near', icon: '📍' },
    { id: 'elite', label: 'Elite', icon: '👑' }
  ];

  const fetchSuggestions = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/community/suggested-connections?category=${category}&limit=5`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      // Fallback to legacy endpoint
      try {
        const legacyResponse = await axios.get(`${API}/community/network-map/suggestions`);
        const allSuggestions = [
          ...(legacyResponse.data.mutual_not_following_back || []),
          ...(legacyResponse.data.popular_in_your_city || []),
          ...(legacyResponse.data.elite_djs || []),
          ...(legacyResponse.data.djs_you_may_know || [])
        ];
        setSuggestions(allSuggestions.slice(0, 5).map(s => ({
          ...s,
          match_percentage: 50,
          reason: 'Suggested'
        })));
      } catch (err) {
        console.error('Failed to fetch suggestions');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions(activeCategory);
    }
  }, [isOpen, activeCategory]);

  const handleFollow = async (userId) => {
    await onFollow(userId);
    setSuggestions(prev => prev.filter(s => s.id !== userId));
  };
  
  return (
    <div 
      className="absolute bottom-4 left-4 z-[1000] rounded-xl shadow-lg overflow-hidden"
      style={{ background: 'rgba(10, 10, 10, 0.95)', border: '1px solid rgba(212, 175, 55, 0.3)', maxWidth: '320px' }}
      data-testid="suggestions-panel"
    >
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#D4AF37' }}>
          <Zap size={16} />
          Suggested Connections
        </h3>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4">
          {/* Category Tabs */}
          <div className="flex gap-1 mb-3">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-2 py-1 rounded-full text-[10px] transition-colors ${
                  activeCategory === cat.id 
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]' 
                    : 'bg-white/5 border border-gray-700'
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          {/* Suggestions List */}
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-4">
                <RefreshCw size={16} className="animate-spin" style={{ color: '#D4AF37' }} />
              </div>
            ) : suggestions.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No suggestions in this category</p>
            ) : (
              suggestions.map(u => (
                <div 
                  key={u.id} 
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{u.dj_name || u.name}</span>
                      <TierBadge tier={u.membership_tier} small />
                    </div>
                    <p className="text-[10px] text-gray-400 truncate">{u.reason || 'Suggested'}</p>
                    {/* Match Score */}
                    {u.match_percentage && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className="w-12 h-1 rounded-full bg-white/10 overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${u.match_percentage}%`, 
                              background: u.match_percentage >= 70 ? '#22c55e' : u.match_percentage >= 50 ? '#D4AF37' : '#3b82f6'
                            }}
                          />
                        </div>
                        <span className="text-[9px] text-gray-500">{u.match_percentage}%</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleFollow(u.id)}
                    className="p-1.5 rounded-lg hover:bg-[#D4AF37]/20 transition-colors ml-2"
                    disabled={isLoading}
                    title="Follow"
                  >
                    <UserPlus size={14} style={{ color: '#D4AF37' }} />
                  </button>
                </div>
              ))
            )}
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={() => fetchSuggestions(activeCategory)}
            className="w-full mt-3 py-1.5 text-xs text-center rounded-lg border border-gray-700 hover:border-[#D4AF37] transition-colors flex items-center justify-center gap-1"
          >
            <RefreshCw size={12} />
            Refresh suggestions
          </button>
        </div>
      )}
    </div>
  );
};

// Legend Component
const MapLegend = () => (
  <div 
    className="absolute bottom-4 right-4 z-[1000] px-4 py-3 rounded-xl shadow-lg"
    style={{ background: 'rgba(10, 10, 10, 0.95)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
  >
    <p className="text-xs text-gray-400 mb-2">Legend</p>
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs">
        <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS.elite, boxShadow: '0 0 8px rgba(212, 175, 55, 0.5)' }} />
        <span>Elite DJ</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS.pro }} />
        <span>Pro DJ</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <div className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS.starter }} />
        <span>Starter DJ</span>
      </div>
      <div className="flex items-center gap-2 text-xs mt-2">
        <div className="w-6 h-0.5" style={{ background: CONNECTION_COLORS.mutual }} />
        <span>Mutual Follow</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <div className="w-6 h-0.5" style={{ background: CONNECTION_COLORS.one_way }} />
        <span>One-way Follow</span>
      </div>
    </div>
  </div>
);

// Main DJNetworkMap Component
const DJNetworkMap = () => {
  const { user } = useAuth();
  const [mapData, setMapData] = useState({ nodes: [], connections: [], stats: null });
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState('my_connections');
  const [showConnections, setShowConnections] = useState(true);
  const [tierFilter, setTierFilter] = useState('all');
  const [statsCollapsed, setStatsCollapsed] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  const isAdmin = user?.is_admin || user?.email === 'contact@blendonics.com';
  const userTier = isAdmin ? 'elite' : (user?.membership_tier || 'starter');

  const fetchMapData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/community/network-map?view_mode=${viewMode}`);
      setMapData(response.data);
    } catch (error) {
      console.error('Failed to fetch map data:', error);
      toast.error('Failed to load network map');
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/network-map/suggestions`);
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, []);

  useEffect(() => {
    fetchMapData();
    fetchSuggestions();
  }, [fetchMapData, fetchSuggestions]);

  const handleFollow = async (userId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/community/follow/${userId}`);
      toast.success(response.data.following ? 'Following!' : 'Unfollowed');
      fetchMapData();
      fetchSuggestions();
    } catch (error) {
      toast.error('Failed to follow');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter nodes based on tier filter
  const filteredNodes = useMemo(() => {
    if (tierFilter === 'all') return mapData.nodes;
    return mapData.nodes.filter(n => n.tier === tierFilter);
  }, [mapData.nodes, tierFilter]);

  // Filter connections based on filtered nodes
  const filteredConnections = useMemo(() => {
    if (!showConnections) return [];
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    return mapData.connections.filter(c => nodeIds.has(c.from_id) && nodeIds.has(c.to_id));
  }, [mapData.connections, filteredNodes, showConnections]);

  // Create a lookup for node positions
  const nodePositions = useMemo(() => {
    const positions = {};
    filteredNodes.forEach(n => {
      positions[n.id] = [n.latitude, n.longitude];
    });
    return positions;
  }, [filteredNodes]);

  if (loading && mapData.nodes.length === 0) {
    return (
      <div className="h-[600px] flex items-center justify-center rounded-2xl" style={{ background: '#0a0a0a', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
        <div className="text-center">
          <Globe size={48} className="mx-auto mb-4 animate-pulse" style={{ color: '#D4AF37' }} />
          <p className="text-lg">Loading DJ Network Map...</p>
        </div>
      </div>
    );
  }

  if (filteredNodes.length === 0) {
    return (
      <div className="h-[600px] flex items-center justify-center rounded-2xl" style={{ background: '#0a0a0a', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
        <div className="text-center max-w-md">
          <MapPin size={48} className="mx-auto mb-4" style={{ color: '#D4AF37' }} />
          <h3 className="text-xl font-bold mb-2">No DJs on the Map Yet</h3>
          <p className="text-gray-400 mb-4">
            Add your location to your DJ profile to appear on the map and connect with DJs worldwide!
          </p>
          <Button
            onClick={async () => {
              try {
                await axios.post(`${API}/community/geocode-location`);
                toast.success('Location updated! Refreshing map...');
                fetchMapData();
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Add a location in your profile first');
              }
            }}
            style={{ background: '#D4AF37', color: '#000' }}
          >
            <Target size={16} className="mr-2" />
            Add My Location
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[600px] rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(212, 175, 55, 0.2)' }}>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: '100%', width: '100%', background: '#0a0a0a' }}
        zoomControl={false}
        minZoom={2}
        maxZoom={12}
      >
        {/* Using OpenStreetMap tiles with dark invert filter applied via CSS */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          className="map-tiles-dark"
        />
        <ZoomControl position="bottomright" />
        
        <MapController nodes={filteredNodes} selectedNode={selectedNode} />
        
        {/* Connection Lines */}
        {filteredConnections.map((conn, idx) => {
          const fromPos = nodePositions[conn.from_id];
          const toPos = nodePositions[conn.to_id];
          if (!fromPos || !toPos) return null;
          
          return (
            <Polyline
              key={`conn-${idx}`}
              positions={[fromPos, toPos]}
              pathOptions={{
                color: conn.is_mutual ? CONNECTION_COLORS.mutual : CONNECTION_COLORS.one_way,
                weight: conn.is_mutual ? 2.5 : 1.5,
                opacity: conn.is_mutual ? 0.8 : 0.5,
                dashArray: conn.is_mutual ? undefined : '5, 5'
              }}
            />
          );
        })}
        
        {/* User Nodes */}
        {filteredNodes.map((node) => {
          const tierColor = TIER_COLORS[node.tier] || TIER_COLORS.starter;
          const isMe = node.is_me;
          const isElite = node.tier === 'elite';
          
          return (
            <CircleMarker
              key={node.id}
              center={[node.latitude, node.longitude]}
              radius={isMe ? 12 : isElite ? 10 : 8}
              pathOptions={{
                color: isMe ? '#D4AF37' : tierColor,
                fillColor: tierColor,
                fillOpacity: isElite ? 0.9 : 0.7,
                weight: isMe ? 3 : isElite ? 2 : 1,
                className: isElite ? 'elite-glow' : ''
              }}
              eventHandlers={{
                click: () => setSelectedNode(node)
              }}
            >
              <Popup>
                <div className="p-2 min-w-[200px]" style={{ background: '#1a1a1a', margin: '-14px', borderRadius: '8px' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${isElite ? 'ring-2 ring-yellow-500' : ''}`}
                      style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
                    >
                      {(node.name || 'D').charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-white">{node.name}</p>
                      <TierBadge tier={node.tier} small />
                    </div>
                  </div>
                  
                  {node.location && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mb-2">
                      <MapPin size={12} />
                      {node.city ? `${node.city}, ${node.country}` : node.location}
                    </p>
                  )}
                  
                  {node.skill_level && (
                    <p className="text-xs text-gray-400 mb-2">Level: {node.skill_level}</p>
                  )}
                  
                  {node.genres?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {node.genres.slice(0, 3).map((g, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                          {g}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-xs mb-2" style={{ color: '#a3a3a3' }}>
                    {node.is_mutual && <span className="text-green-500">🔁 Mutual</span>}
                    {node.is_following && !node.is_mutual && <span className="text-blue-400">Following</span>}
                    {node.is_follower && !node.is_mutual && <span className="text-purple-400">Follows you</span>}
                  </div>
                  
                  {!isMe && (
                    <Button
                      size="sm"
                      className="w-full mt-2"
                      onClick={(e) => { e.stopPropagation(); handleFollow(node.id); }}
                      disabled={actionLoading}
                      style={{ 
                        background: node.is_following ? 'transparent' : '#D4AF37', 
                        color: node.is_following ? '#D4AF37' : '#000',
                        border: '1px solid #D4AF37'
                      }}
                    >
                      {node.is_following ? 'Unfollow' : 'Follow'}
                    </Button>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
      
      {/* Overlay Panels */}
      <StatsPanel 
        stats={mapData.stats} 
        isCollapsed={statsCollapsed} 
        onToggle={() => setStatsCollapsed(!statsCollapsed)} 
      />
      
      <FilterPanel
        viewMode={viewMode}
        setViewMode={setViewMode}
        showConnections={showConnections}
        setShowConnections={setShowConnections}
        tierFilter={tierFilter}
        setTierFilter={setTierFilter}
        accessLevel={mapData.access_level || userTier}
        onRefresh={fetchMapData}
      />
      
      <SuggestionsPanel 
        onFollow={handleFollow}
        isLoading={actionLoading}
        onRefresh={() => { fetchMapData(); }}
      />
      
      <MapLegend />
      
      {/* CSS for elite glow effect and dark map tiles */}
      <style>{`
        .elite-glow {
          filter: drop-shadow(0 0 6px rgba(212, 175, 55, 0.6));
        }
        .map-tiles-dark {
          filter: invert(100%) hue-rotate(180deg) brightness(0.85) contrast(0.9);
        }
        .leaflet-popup-content-wrapper {
          background: #1a1a1a !important;
          border: 1px solid rgba(212, 175, 55, 0.3) !important;
          border-radius: 12px !important;
          padding: 0 !important;
        }
        .leaflet-popup-tip {
          background: #1a1a1a !important;
        }
        .leaflet-popup-close-button {
          color: #fff !important;
        }
      `}</style>
    </div>
  );
};

export default DJNetworkMap;
